import { Component, createContext, useContext, useEffect, useRef } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { omit } from 'lodash-es'
import PropTypes from 'prop-types'
import cc from 'classcat'

import { CartContext } from './CartContext'
import { REDEEM_COUPON_FAILURE, setCartCouponCode, setCartCouponError } from '../../../store/actions'
import { useTranslate } from '../../../utils/translate'

export const CouponContext = createContext()

// https://github.com/oliviertassinari/babel-plugin-transform-react-remove-prop-types/blob/v0.4.15/README.md#is-it-safe
export const RedemptionFormRawPropTypes = {
  cart: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  showCouponError: PropTypes.func.isRequired,
  hideCouponError: PropTypes.func.isRequired,
  isCartBusy: PropTypes.bool,
  couponCode: PropTypes.string,
  errorNotificationTimeout: PropTypes.number,
  hasCartCouponError: PropTypes.bool,
}

export class RedemptionFormRaw extends Component {
  static propTypes = RedemptionFormRawPropTypes

  static defaultProps = {
    errorNotificationTimeout: 5000,
    isCartBusy: false,
  }

  static contextType = CartContext

  errorNoticeAlreadyDisplayed = false

  couponFieldErrorClass = false

  componentDidUpdate(prevProps) {
    const { cart, couponCode } = this.props

    // hide the error notification as soon as the user is typing
    if (couponCode !== prevProps.couponCode) {
      this.couponFieldErrorClass = false
      return this.hideErrorNotification()
    }

    if (cart.couponError && !prevProps.hasCartCouponError && !this.errorNoticeAlreadyDisplayed) {
      this.props.showCouponError()
      this.errorNoticeAlreadyDisplayed = true
      this.couponFieldErrorClass = true

      // if there's a timer running while a new notification is displayed then
      // prevent this old timer from hiding the new notification (too soon / instantly)
      if (this.timer) window.clearTimeout(this.timer)

      // start new cancelable timer
      this.timer = window.setTimeout(this.hideErrorNotification, this.props.errorNotificationTimeout)
    }
  }

  componentWillUnmount() {
    if (this.timer) window.clearTimeout(this.timer)

    // Reset coupon code input value.
    this.props.dispatch(setCartCouponCode(''))
  }

  hideErrorNotification = () => {
    if (this.props.hasCartCouponError) {
      this.props.hideCouponError()
    }
  }

  onSubmit = (event) => {
    event.preventDefault()
    event.stopPropagation()

    const couponCode = event.target.elements.couponCode.value.trim()
    if (couponCode.length === 0) {
      // dispatch the same action that is dispatched by error responses
      // from the coupon API.
      // The `errorMessage` follows the format of errorsfrom the API.
      this.props.dispatch({
        type: REDEEM_COUPON_FAILURE,
        errorMessage: '/coupon failed Reason: CouponIsEmpty',
      })
      return false
    }

    this.context
      .redeemCoupon(couponCode)
      // prevent error notification from being shown again before the failed response has even arrived
      .catch(() => {
        this.errorNoticeAlreadyDisplayed = false
      })
  }

  render() {
    if (!this.props.cart.canHaveCoupon) return null

    const htmlAttributes = omit(this.props, Object.keys(RedemptionFormRawPropTypes))

    return (
      <CouponContext.Provider value={this}>
        <form {...htmlAttributes} method="post" onSubmit={this.onSubmit} />
      </CouponContext.Provider>
    )
  }
}

export function CouponField({ className, errorClass, ...htmlAttributes }) {
  const dispatch = useDispatch()
  const couponCode = useSelector((state) => state.getIn(['view', 'cart', 'couponCode'], ''))
  const hasCartCouponError = useSelector((state) => state.getIn(['view', 'cart', 'hasCouponError']))
  const handleChange = (event) => dispatch(setCartCouponCode(event.target.value))

  const redemptionForm = useContext(CouponContext)

  return (
    <input
      required
      type="text"
      {...htmlAttributes}
      name="couponCode"
      value={couponCode}
      onChange={handleChange}
      className={cc([
        className,
        {
          [errorClass]: hasCartCouponError || redemptionForm.couponFieldErrorClass,
        },
      ])}
    />
  )
}

CouponField.propTypes = {
  className: PropTypes.string,
  errorClass: PropTypes.string,
}

export function RedeemButton(htmlAttributes) {
  const couponCode = useSelector((state) => state.getIn(['view', 'cart', 'couponCode']))
  const isCartBusy = useSelector((state) => state.getIn(['view', 'cart', 'isBusy'], false))

  return <button {...htmlAttributes} type="submit" disabled={!couponCode || isCartBusy} />
}

export function RemoveCouponButton(htmlAttributes) {
  const cart = useSelector((state) => state.get('cart').toJS())
  const isCartBusy = useSelector((state) => state.getIn(['view', 'cart', 'isBusy'], false))
  const cartContext = useContext(CartContext)

  if (!cart.coupon) return null

  return <button {...htmlAttributes} type="button" disabled={isCartBusy} onClick={() => cartContext.removeCoupon()} />
}

export function CouponError({ className, hiddenClass, ...props }) {
  const { couponError } = useSelector((state) => state.get('cart').toJS())
  const hasCartCouponError = useSelector((state) => state.getIn(['view', 'cart', 'hasCouponError']))

  const t = useTranslate('shop')

  const errorText = couponError
    ? t(`views.storefrontView.basketPageSection.couponInputField.validationMessages.${couponError.id}`, {
        ...couponError.params,
      })
    : ''

  const showError = hasCartCouponError && errorText
  const htmlAttributes = omit(props, ['tReady', 'i18n'])
  return (
    <div {...htmlAttributes} className={cc([className, { [hiddenClass]: !showError }])}>
      {errorText}
    </div>
  )
}

CouponError.propTypes = {
  className: PropTypes.string,
  hiddenClass: PropTypes.string,
}

export function ValidationInfo({ errorClass, warningClass, className, ...htmlAttributes }) {
  const context = useContext(CartContext)

  const validationErrors = useSelector((state) =>
    state.hasIn(['cart', 'coupon', 'validationErrors'])
      ? state.getIn(['cart', 'coupon', 'validationErrors']).toJS()
      : [],
  )
  const minimumOrderValue = useSelector((state) =>
    state.getIn(['cart', 'couponCampaign', 'minimumOrderValue', 'formatted']),
  )
  const prevErrors = useRef(validationErrors)

  const t = useTranslate('shop')

  useEffect(() => {
    // if there is a new validation error, scroll to the error message
    if (
      validationErrors.length > 0 &&
      (prevErrors.current.length !== validationErrors.length || prevErrors.current[0] !== validationErrors[0])
    ) {
      prevErrors.current = validationErrors
      window.scrollTo(0, 0)
    }
  })

  // nothing to do here if there is no error
  if (validationErrors.length === 0) {
    return null
  }

  const hasInvalidCoupon = context.hasInvalidCoupon()

  const firstValidationError = validationErrors[0]

  const classNames = cc([
    className,
    {
      [errorClass]: hasInvalidCoupon,
      [warningClass]: !hasInvalidCoupon,
    },
  ])

  // The LineItemsSubTotalTooSmall error is a special case with a parameter for the translation
  if (firstValidationError === 'LineItemsSubTotalTooSmall') {
    return (
      <div {...htmlAttributes} className={classNames}>
        <span>
          {t('views.storefrontView.basketPageSection.couponInputField.validationMessages.LineItemsSubTotalTooSmall', {
            value: minimumOrderValue,
          })}
        </span>
      </div>
    )
  }

  // any other case is a simple validation error
  return (
    <div {...htmlAttributes} className={classNames}>
      <span>
        {t(`views.storefrontView.basketPageSection.couponInputField.validationMessages.${firstValidationError}`)}
      </span>
    </div>
  )
}

ValidationInfo.propTypes = {
  className: PropTypes.string,
  errorClass: PropTypes.string,
  warningClass: PropTypes.string,
}

export const RedemptionForm = connect(
  (state) => ({
    cart: state.get('cart').toJS(),
    couponCode: state.getIn(['view', 'cart', 'couponCode']),
    hasCartCouponError: state.getIn(['view', 'cart', 'hasCouponError']),
    isCartBusy: state.getIn(['view', 'cart', 'isBusy']),
  }),
  (dispatch) => ({
    dispatch,
    showCouponError: () => dispatch(setCartCouponError(true)),
    hideCouponError: () => dispatch(setCartCouponError(false)),
  }),
)(RedemptionFormRaw)

const CouponComponents = {
  RedemptionForm: Object.assign(RedemptionForm, {
    CouponField,
    RedeemButton,
  }),
  ValidationInfo,
  CouponError,
  RemoveCouponButton,
}

export default CouponComponents
